import { z } from "zod";

import {
  nullToUndefined,
  extractUnknownArray,
} from "./phase2-api-parse-utils.ts";
import {
  phase2PendingBankDetailsSchema,
  type Phase2PendingBankDetails,
} from "./phase2-pending-row.schema.ts";
export const phase2SummarySchema = z.object({
  total: z.coerce.number(),
  approved: z.coerce.number(),
  rejected: z.coerce.number(),
  pending: z.coerce.number(),
});

function unwrapPhase2ListPayload(raw: unknown): unknown {
  if (raw == null || typeof raw !== "object") return raw;
  const root = raw as Record<string, unknown>;
  const inner = root.data;
  if (
    inner != null &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    ("summary" in (inner as Record<string, unknown>) ||
      "data" in (inner as Record<string, unknown>))
  ) {
    return inner;
  }
  return raw;
}

function parseSummaryObject(raw: unknown): ApprovalCenterStats | null {
  if (raw == null || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  const num = (value: unknown): number | null => {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const total = num(s.total ?? s.totalCount ?? s.totalRequests);
  const pending = num(s.pending ?? s.pendingCount);
  const pendingPreJoining = num(s.pendingPreJoining);
  const approved = num(s.approved ?? s.approvedCount);
  const rejected = num(s.rejected ?? s.rejectedCount);
  const pendingPostJoining = num(s.pendingPostJoining);
  const pendingReview = num(s.pendingReview);
  const pendingInsurance = num(s.pendingInsurance);
  const needsCorrection = num(s.needsCorrection);
  const workflowApproved = num(s.workflowApproved);
  if (
    total == null &&
    pending == null &&
    pendingPreJoining == null &&
    approved == null &&
    rejected == null
  ) {
    return null;
  }
  return {
    total: total ?? 0,
    pending: pending ?? 0,
    pendingPostJoining: pendingPostJoining ?? 0,
    pendingReview: pendingReview ?? 0,
    pendingInsurance: pendingInsurance ?? 0,
    needsCorrection: needsCorrection ?? 0,
    approved: workflowApproved ?? approved ?? 0,
    rejected: rejected ?? 0,
    pendingPreJoining: pendingPreJoining ?? 0,

  };
}

const phase2DocumentSchemaInner = z
  .object({
    documentId: z.union([z.number(), z.string()]),
    docType: z.string().nullish(),
    path: z.string().nullish(),
    status: z.string().nullish(),
    approvalStatus: z.string().nullish(),
    reason: z.string().nullish(),
    documentPath: z.string().nullish(),
    approveDate: z.string().nullish(),
  })
  .passthrough();

export const phase2DocumentSchema = z.preprocess(
  nullToUndefined,
  phase2DocumentSchemaInner,
);

const phase2EmployeeSchemaInner = z
  .object({
    id: z.union([z.number(), z.string()]),
    employeeCode: z.string().nullish(),
    employeeType: z.string().nullish(),
    employeeShift: z.string().nullish(),
    maritalStatus: z.string().nullish(),
    firstName: z.string().nullish(),
    middleName: z.string().nullish(),
    lastName: z.string().nullish(),
    personalEmail: z.string().nullish(),
    ccEmail: z.string().nullish(),
    photoPath: z.string().nullish(),
    designation: z.string().nullish(),
    managerName: z.string().nullish(),
    leaveManagerName: z.string().nullish(),
    phase2CreatedOn: z.string().nullish(),
    doj: z.string().nullish(),
    documents: z.array(phase2DocumentSchema).nullish(),
  })
  .passthrough();

export const phase2EmployeeSchema = z.preprocess(
  nullToUndefined,
  phase2EmployeeSchemaInner,
);

const phase2InsuranceDetailsSchema = z
  .object({
    id: z.union([z.number(), z.string()]).nullish(),
    insuranceNumber: z.string().nullish(),
    insuranceProviderName: z.string().nullish(),
    insuranceSoftCopy: z.string().nullish(),
    insuranceFrom: z.string().nullish(),
    insuranceTo: z.string().nullish(),
    status: z.string().nullish(),
  })
  .passthrough();

const phase2FamilyInsuranceMemberSchema = z.object({
  insuranceNumber: z.string().nullish(),
  insuranceSoftCopy: z.string().nullish(),
  insuranceFrom: z.string().nullish(),
  insuranceTo: z.string().nullish(),
}).passthrough();

const phase2FamilyInsuranceDetailsSchema = z.object({
  spouseInsurance: phase2FamilyInsuranceMemberSchema.nullish(),
  childInsurance: phase2FamilyInsuranceMemberSchema.nullish(),
}).passthrough();

const phase2ListItemSchemaInner = z
  .object({
    phase2Id: z.union([z.number(), z.string()]).nullish(),
    phase2CreatedOn: z.string().nullish(),
    submittedOn: z.string().nullish(),
    status: z.string().nullish(),
    approvalStatus: z.string().nullish(),
    finalStatus: z.string().nullish(),
    workflowStatus: z.string().nullish(),
    Doj: z.string().nullish(),
    employee: phase2EmployeeSchema.nullish(),
    isBankDetailsAvailable: z.boolean().nullish(),
    isInsuranceAvailable: z.boolean().nullish(),
    isInsuranceDetails: z.boolean().nullish(),
    insuranceStatus: z.string().nullish(),
    insuranceDetails: phase2InsuranceDetailsSchema.nullish(),
    familyInsuranceDetails: phase2FamilyInsuranceDetailsSchema.nullish(),
    childStatus: z.boolean().nullish(),
    bankStatus: z.string().nullish(),
    bankDetails: phase2PendingBankDetailsSchema.nullish(),
    documentCount: z.coerce.number().nullish(),
    documents: z.array(phase2DocumentSchema).nullish(),
    employeeId: z.union([z.number(), z.string()]).nullish(),
    employeeName: z.string().nullish(),
    employeeCode: z.string().nullish(),
    email: z.string().nullish(),
    maritalStatus: z.string().nullish(),
    employeeShift: z.string().nullish(),
  })
  .passthrough();

export const phase2ListItemSchema = z.preprocess(
  nullToUndefined,
  phase2ListItemSchemaInner,
);

export type Phase2Employee = z.infer<typeof phase2EmployeeSchemaInner>;
export type Phase2Document = z.infer<typeof phase2DocumentSchemaInner>;
export type Phase2ListItem = z.infer<typeof phase2ListItemSchemaInner>;
export type Phase2InsuranceDetails = z.infer<
  typeof phase2InsuranceDetailsSchema
>;
export type Phase2FamilyInsuranceDetails = z.infer<typeof phase2FamilyInsuranceDetailsSchema>;

export type ApprovalCenterStats = {
  total: number;
  pending: number;
  pendingPostJoining: number;
  pendingReview: number;
  pendingInsurance: number;
  needsCorrection: number;
  approved: number;
  rejected: number;
  pendingPreJoining: number;
};

/** Normalized row used by the approval center page and modal. */
export type Phase2ListRow = {
  phase2Id: number;
  phase2CreatedOn?: string;
  submittedOn: string;
  status: string;
  approvalStatus: string;
  finalStatus: string;
  workflowStatus: string;
  employeeId: number;
  employeeName: string;
  employeeCode: string;
  employeeType?: string;
  employeeShift?: string;
  maritalStatus?: string;
  email: string;
  photoPath?: string;
  designation?: string;
  managerName?: string;
  leaveManagerName?: string;
  doj?: string;
  isBankDetailsAvailable: boolean;
  isInsuranceAvailable: boolean;
  insuranceStatus?: string;
  insuranceDetails?: Phase2InsuranceDetails;
  familyInsuranceDetails?: Phase2FamilyInsuranceDetails;
  childStatus: boolean;
  bankStatus?: string;
  bankDetails: Phase2PendingBankDetails;
  documentCount: number;
  documents: Phase2Document[];
};

const EMPTY_STATS: ApprovalCenterStats = {
  total: 0,
  pending: 0,
  pendingPostJoining: 0,
  pendingReview: 0,
  pendingInsurance: 0,
  needsCorrection: 0,
  approved: 0,
  rejected: 0,
  pendingPreJoining: 0,

};

function buildEmployeeName(employee: Phase2Employee | null | undefined): string {
  if (employee == null) return "";
  const parts = [employee.firstName, employee.middleName, employee.lastName]
    .map((p) => (p ?? "").trim())
    .filter(Boolean);
  return parts.join(" ");
}

function normalizeDocument(doc: Phase2Document): Phase2Document {
  return {
    ...doc,
    path: doc.path ?? doc.documentPath ?? undefined,
    status: doc.status ?? doc.approvalStatus ?? undefined,
  };
}

function parseDocumentEntry(raw: unknown): Phase2Document | null {
  const parsed = phase2DocumentSchema.safeParse(raw);
  if (!parsed.success) return null;
  if (parsed.data.documentId == null || parsed.data.documentId === "") {
    return null;
  }
  return normalizeDocument(parsed.data);
}

function collectDocuments(item: Phase2ListItem): Phase2Document[] {
  const fromEmployee = item.employee?.documents ?? [];
  const fromItem = item.documents ?? [];
  const merged = [...fromEmployee, ...fromItem]
    .map((doc) => parseDocumentEntry(doc))
    .filter((doc): doc is Phase2Document => doc != null);
  const seen = new Set<string>();
  return merged.filter((doc) => {
    const key = String(doc.documentId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function parseListItemLenient(raw: unknown): Phase2ListRow | null {
  const parsed = phase2ListItemSchema.safeParse(raw);
  if (parsed.success) return normalizePhase2ListItem(parsed.data);

  if (raw == null || typeof raw !== "object") return null;
  const record = nullToUndefined(raw) as Record<string, unknown>;
  const employee =
    record.employee != null && typeof record.employee === "object"
      ? (nullToUndefined(record.employee) as Record<string, unknown>)
      : null;

  const phase2Id = record.phase2Id ?? record.id ?? employee?.id;
  const employeeId = employee?.id ?? record.employeeId;
  if (phase2Id == null || phase2Id === "" || employeeId == null || employeeId === "") {
    return null;
  }

  const fallbackParsed = phase2ListItemSchema.safeParse({
    ...record,
    phase2Id,
    employeeId,
    employee: employee ?? undefined,
  });
  if (fallbackParsed.success) return normalizePhase2ListItem(fallbackParsed.data);
  return null;
}

function parseListItems(rawItems: unknown[]): Phase2ListRow[] {
  const rows: Phase2ListRow[] = [];
  for (const item of rawItems) {
    const row = parseListItemLenient(item);
    if (row != null) rows.push(row);
  }
  return rows;
}

function parsePagedPayload(
  payloadRecord: Record<string, unknown>,
): Phase2ListParseResult | null {
  const data = extractUnknownArray(payloadRecord);
  if (data == null) return null;

  const rows = parseListItems(data);
  const summary =
    parseSummaryObject(payloadRecord.summary) ?? EMPTY_STATS;

  return {
    rows,
    summary,
    totalElements: resolveTotalElements(
      {
        totalElements: payloadRecord.totalElements as number | undefined,
        totalCount: payloadRecord.totalCount as number | undefined,
        summary,
      },
      rows.length,
    ),
    currentPage:
      typeof payloadRecord.currentPage === "number"
        ? payloadRecord.currentPage
        : undefined,
    pageSize:
      typeof payloadRecord.pageSize === "number"
        ? payloadRecord.pageSize
        : undefined,
    totalPages:
      typeof payloadRecord.totalPages === "number"
        ? payloadRecord.totalPages
        : undefined,
  };
}

export function normalizePhase2ListItem(item: Phase2ListItem): Phase2ListRow {
  const employee = item.employee ?? null;
  const employeeId = Number(employee?.id ?? item.employeeId ?? 0);
  const employeeName =
    (item.employeeName ?? buildEmployeeName(employee)).trim() || "—";
  const employeeCode =
    (employee?.employeeCode ?? item.employeeCode ?? "").trim() || "—";
  const email =
    (employee?.personalEmail ?? employee?.ccEmail ?? item.email ?? "").trim() ||
    "—";
  const documents = collectDocuments(item);

  const phase2CreatedOn =
    item.phase2CreatedOn ??
    employee?.phase2CreatedOn ??
    undefined;

  return {
    phase2Id: Number(item.phase2Id ?? employeeId),
    phase2CreatedOn,
    submittedOn: item.submittedOn ?? "",
    status: item.status ?? "",
    finalStatus: item.finalStatus ?? "",
    workflowStatus: item.workflowStatus ?? item.approvalStatus ?? "",
    approvalStatus:
      item.approvalStatus ??
      item.bankDetails?.approvalStatus ??
      item.bankStatus ??
      "",
    employeeId,
    employeeName,
    employeeCode,
    employeeType: employee?.employeeType ?? undefined,
    employeeShift: employee?.employeeShift ?? item.employeeShift ?? undefined,
    maritalStatus: employee?.maritalStatus ?? item.maritalStatus ?? undefined,
    email,
    photoPath: employee?.photoPath ?? undefined,
    designation: employee?.designation ?? undefined,
    managerName: employee?.managerName ?? undefined,
    leaveManagerName: employee?.leaveManagerName ?? undefined,
    doj: employee?.doj ?? item.Doj ?? undefined,
    isBankDetailsAvailable: item.isBankDetailsAvailable === true,
    isInsuranceAvailable:
      item.isInsuranceAvailable === true || item.isInsuranceDetails === true,
    insuranceStatus: item.insuranceStatus ?? undefined,
    insuranceDetails: item.insuranceDetails ?? undefined,
    familyInsuranceDetails: item.familyInsuranceDetails ?? undefined,
    childStatus: item.childStatus === true,
    bankStatus: item.bankStatus ?? undefined,
    bankDetails: item.bankDetails ?? {},
    documentCount: item.documentCount ?? documents.length,
    documents,
  };
}

export type Phase2ListParseResult = {
  rows: Phase2ListRow[];
  summary: ApprovalCenterStats;
  totalElements: number;
  currentPage?: number;
  pageSize?: number;
  totalPages?: number;
};

function resolveTotalElements(
  meta: {
    totalElements?: number;
    totalCount?: number;
    summary?: ApprovalCenterStats;
  },
  rowCount: number,
): number {
  if (meta.totalElements != null && Number.isFinite(meta.totalElements)) {
    return meta.totalElements;
  }
  if (meta.totalCount != null && Number.isFinite(meta.totalCount)) {
    return meta.totalCount;
  }
  if (meta.summary?.total != null && Number.isFinite(meta.summary.total)) {
    return meta.summary.total;
  }
  return rowCount;
}

export function parsePhase2ListResponse(raw: unknown): Phase2ListParseResult {
  const empty: Phase2ListParseResult = {
    rows: [],
    summary: EMPTY_STATS,
    totalElements: 0,
  };

  const payload = unwrapPhase2ListPayload(raw);
  const payloadRecord =
    payload != null && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : null;

  if (payloadRecord) {
    const paged = parsePagedPayload(payloadRecord);
    if (paged != null) return paged;
  }

  const summaryFromPayload = payloadRecord
    ? parseSummaryObject(payloadRecord.summary)
    : null;

  const listArray = payloadRecord ? extractUnknownArray(payloadRecord) : null;
  if (listArray != null) {
    const rows = parseListItems(listArray);
    return {
      rows,
      summary: summaryFromPayload ?? EMPTY_STATS,
      totalElements: resolveTotalElements(
        {
          totalElements: payloadRecord.totalElements as number | undefined,
          totalCount: payloadRecord.totalCount as number | undefined,
          summary: summaryFromPayload ?? EMPTY_STATS,
        },
        rows.length,
      ),
    };
  }

  const asArray = z.array(z.unknown()).safeParse(payload);
  if (asArray.success) {
    const rows = parseListItems(asArray.data);
    return {
      rows,
      summary: summaryFromPayload ?? EMPTY_STATS,
      totalElements: rows.length,
    };
  }

  if (summaryFromPayload != null) {
    return { rows: [], summary: summaryFromPayload, totalElements: 0 };
  }

  return empty;
}

/** @deprecated Use parsePhase2ListResponse */
export function parsePhase2List(raw: unknown): Phase2ListRow[] {
  return parsePhase2ListResponse(raw).rows;
}
